<script lang="ts">
  import { onDestroy } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faSave, faTimes, faArrowDownAZ } from '@fortawesome/free-solid-svg-icons';
  import { Button } from 'flowbite-svelte';
  import CustomImage from '../../common/CustomImage.svelte';
  import { createGrid } from 'ag-grid-community';
  import type { GridApi, GridOptions } from 'ag-grid-community';
  import 'ag-grid-community/styles/ag-grid.css';
  import 'ag-grid-community/styles/ag-theme-quartz.css';
  import { get } from 'svelte/store';
  import { studyExtractStore, projectStructureStore } from './stores.js';
  import { saveTargetExtract } from '../data/target-data-handler.js';
  import {
    ensureGrouperStructureExists,
    ensureGrouperArray,
    ensureGrouperRows,
    createEmptyGrouperRow
  } from './grouper-migration.js';
  import { syncGrouperRowToTables, syncGrouperRowsToTables } from './grouper-table-sync.js';
  import { getDisplayName } from './grouper-utils.js';
  import { getCellEditorConfig, getLookupValueGetterSetter, getLookupCellRenderer } from './cell-editor-config.js';

  export let show: boolean = false;
  export let topicId: string = 'baseline';

  let gridDiv: HTMLDivElement;
  let gridApi: GridApi | null = null;
  let gridInstance: any = null;

  let columnDefs: any[] = [];
  let rowData: any[] = [];
  let selectedRows: Set<number> = new Set();
  let hasDuplicateRows = false;
  let lastEditedGrouperId: string | null = null;
  const unsavedGrouperIds: Set<string> = new Set();
  let currentSortPrimaryLevelIndex: number | null = null;
  let lastSortTopicId: string | null = null;
  let lastInitKey: string | null = null;

  // Removed custom popup state - using agRichSelectCellEditor instead

  $: $studyExtractStore = get(studyExtractStore);
  $: $projectStructureStore = get(projectStructureStore);

  $: rowRemoveEnabled = selectedRows.size > 0;

  // Initialize grid only when dialog/topic changes (not on every data change)
$: {
    const key = show && topicId ? String(topicId) : '';
    if (show && key && key !== lastInitKey) {
      lastInitKey = key;
      setTimeout(() => {
        initializeGrid();
      }, 100);
    }
  }

function getSourceFieldName(source: any): string {
  if (!source) return '';
  if (source.refTable) {
    return String(source.refTable);
  }
  if (source.type === 'custom') {
    return 'custom';
  }
  if (source.refType) {
    return String(source.refType)
      .toLowerCase()
      .replace(/\s+/g, '_');
  }
  return 'field';
}

function assertTopicExists(topic: any, topicId: string): asserts topic is any {
  if (!topic) {
    throw new Error(`[grouper] Topic "${topicId}" not found in study extract store`);
  }
}

function resolveReferenceValue(
  study: any,
  refTable: string | undefined,
  columnId: string | undefined,
  refId: string | undefined
): string {
  if (!study || !refTable || !refId) return refId || '';

  const collection = study.references?.find((ref: any) => ref.id === refTable);
  if (!collection) {
    return refId;
  }

  // Get the reference structure to find the nameRef field
  const structure = get(projectStructureStore);
  const refStructure = structure?.references?.find((ref: any) => ref.id === refTable);
  const nameRef = refStructure?.nameRef;

  if (collection.rows) {
    const row = collection.rows.find((r: any) => r.id === refId);
    if (row?.cells) {
      // Use nameRef if specified, otherwise use columnId, then fall back to 'name' or 'title'
      let nameCell;
      if (nameRef) {
        // Use the nameRef field specified in the reference structure
        nameCell = row.cells.find((c: any) => c.id === nameRef);
      } else if (columnId) {
        // Use the specified columnId
        nameCell = row.cells.find((c: any) => c.id === columnId);
      } else {
        // Fallback to 'name' or 'title' for backward compatibility
        nameCell = row.cells.find((c: any) => c.id === 'name') ||
                   row.cells.find((c: any) => c.id === 'title');
      }
      if (nameCell?.value) {
        return nameCell.value;
      }
    }
  }

  if (collection.values) {
    const valueEntry = collection.values.find((v: any) => v.id === refId);
    if (valueEntry?.value) {
      return valueEntry.value;
    }
  }

  return refId;
}

function getLevelSortValues(row: any, level: any, study: any): string[] {
  const values: string[] = [];
  const levelNumber = String(level.level ?? level.number ?? '');
  const rowLevel = row?.levels?.find(
    (lvl: any) => String(lvl.level ?? lvl.number ?? '') === levelNumber
  );

  const referenceSources = (level.sources || []).filter(
    (source: any) => source?.type === 'reference'
  );

  referenceSources.forEach((source: any, index: number) => {
    const refs = Array.isArray(rowLevel?.refs) ? rowLevel.refs : [];
    const refId = refs[index] || '';
    if (!refId) {
      values.push('');
      return;
    }

    const displayValue = resolveReferenceValue(
      study,
      source.refTable,
      source.refId,
      refId
    );
    values.push(displayValue || '');
  });

  return values;
}

function normalizeSortValue(value: string): string {
  if (!value) return '';
  return value.toString().trim().toLowerCase();
}

function compareSortValues(a: string, b: string): number {
  const aValue = normalizeSortValue(a);
  const bValue = normalizeSortValue(b);

  const aEmpty = aValue === '';
  const bEmpty = bValue === '';

  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return -1;
  if (bEmpty) return 1;

  return aValue.localeCompare(bValue, undefined, { sensitivity: 'base' });
}

function getCustomValue(row: any): string {
  if (!row?.levels) return '';
  const customLevel = row.levels.find((lvl: any) => {
    const identifier = String(lvl.level ?? lvl.number ?? '').toLowerCase();
    return identifier === 'custom';
  });

  if (typeof customLevel?.value === 'string') {
    return customLevel.value.trim();
  }

  if (typeof row.custom === 'string') {
    return row.custom.trim();
  }

  return '';
}

function getRowSortValues(row: any, grouperStructure: any, study: any): string[] {
  const values: string[] = [];
  (grouperStructure?.levels || []).forEach((level: any) => {
    values.push(...getLevelSortValues(row, level, study));
  });
  const custom = getCustomValue(row);
  values.push(custom);
  return values;
}

function getRowSortValuesByLevel(
  row: any,
  grouperStructure: any,
  study: any
): string[][] {
  const levelValues: string[][] = [];
  (grouperStructure?.levels || []).forEach((level: any) => {
    levelValues.push(getLevelSortValues(row, level, study));
  });
  return levelValues;
}

function getReferenceSources(levelStructure: any): any[] {
  if (!levelStructure?.sources) {
    return [];
  }
  return levelStructure.sources.filter((source: any) => source?.type === 'reference');
}

function ensureRowLevels(row: any): void {
  if (!row.levels || !Array.isArray(row.levels)) {
    row.levels = [];
  }
}

function ensureRowLevelEntry(row: any, levelNumber: string, referenceSources: any[]): any {
  ensureRowLevels(row);

  let levelEntry = row.levels.find(
    (lvl: any) => String(lvl.level ?? lvl.number ?? '') === levelNumber
  );

  if (!levelEntry) {
    levelEntry = {
      level: levelNumber,
      refs: []
    };
    row.levels.push(levelEntry);
  }

  if (!Array.isArray(levelEntry.refs)) {
    levelEntry.refs = [];
  }

  while (levelEntry.refs.length < referenceSources.length) {
    levelEntry.refs.push('');
  }

  if (levelEntry.refs.length > referenceSources.length) {
    levelEntry.refs.length = referenceSources.length;
  }

  return levelEntry;
}

function setCustomValue(row: any, value: string): void {
  ensureRowLevels(row);
  const trimmed = value.trim();
  const existingIndex = row.levels.findIndex((lvl: any) => {
    const identifier = String(lvl.level ?? lvl.number ?? '').toLowerCase();
    return identifier === 'custom';
  });

  if (trimmed === '') {
    if (existingIndex !== -1) {
      row.levels.splice(existingIndex, 1);
    }
    delete row.custom;
    return;
  }

  if (existingIndex === -1) {
    row.levels.push({
      level: 'custom',
      value: trimmed
    });
  } else {
    row.levels[existingIndex] = {
      level: 'custom',
      value: trimmed
    };
  }

  row.custom = trimmed;
}

function createRowDataFromGrouperRow(
  grouperRow: any,
  index: number,
  grouperStructure: any,
  study: any
): any {
  const rowNumber =
    typeof grouperRow?.order === 'number' && !Number.isNaN(grouperRow.order)
      ? grouperRow.order
      : index + 1;

  // Get display name for the grouper row
  const displayName = getDisplayName(grouperRow, study, { grouperStructure });
  
  const grouperIdKey = grouperRow?.grouperId ? String(grouperRow.grouperId) : '';
  const rowDataObj: any = {
    row: rowNumber.toString(),
    _rowIndex: index,
    __grouperRowId: grouperRow?.grouperId ? String(grouperRow.grouperId) : '',
    grouperIdDisplay: grouperRow?.grouperId ? String(grouperRow.grouperId) : '-',
    nameDisplay: displayName || 'Study' 
  };
  const customValueForRow = getCustomValue(grouperRow);
  rowDataObj.custom = customValueForRow || '-';

  // Read grouper cell values from cells array
  const grouperCells = Array.isArray(grouperRow?.cells) ? grouperRow.cells : [];
  const rootGrouperColumns = $projectStructureStore?.grouper?.columns || [];
  rootGrouperColumns.forEach((columnDef: any) => {
    const cell = grouperCells.find((c: any) => c.id === columnDef.id);
    const cellValue = cell?.value || '';
    rowDataObj[columnDef.id] = cellValue || '-';
  });

  const structureLevels = grouperStructure?.levels || [];
  structureLevels.forEach((level: any, levelIndex: number) => {
    const levelNumber = String(level.level ?? level.number ?? levelIndex + 1);
    const rowLevel = Array.isArray(grouperRow?.levels)
      ? grouperRow.levels.find(
          (lvl: any) => String(lvl.level ?? lvl.number ?? '') === levelNumber
        )
      : null;
    const referenceSources = getReferenceSources(level);
    const refs = Array.isArray(rowLevel?.refs) ? rowLevel.refs : [];

    referenceSources.forEach((source: any, refIndex: number) => {
      const fieldName = getSourceFieldName(source);
      const refValue = refs[refIndex] || '';
      const displayValue = refValue
        ? resolveReferenceValue(study, source.refTable, source.refId, refValue)
        : '-';
      rowDataObj[fieldName] = refValue || '-';
      rowDataObj[`${fieldName}Display`] = displayValue || '-';
    });

    if (Array.isArray(level?.columns)) {
      level.columns.forEach((columnDef: any) => {
        rowDataObj[columnDef.id] = '-';
      });
    }

    if (Array.isArray(level?.sources)) {
      level.sources
        .filter((source: any) => source?.type === 'custom')
        .forEach((source: any) => {
          const fieldName = getSourceFieldName(source) || 'custom';
          rowDataObj[fieldName] = customValueForRow || '-';
        });
    }
  });

  const sortValues = getRowSortValues(grouperRow, grouperStructure, study);
  rowDataObj.__rowKey = sortValues.map(normalizeSortValue).join('|');
  rowDataObj.__duplicate = false;
  rowDataObj.__isNew = grouperIdKey ? unsavedGrouperIds.has(grouperIdKey) : false;

  return rowDataObj;
}

function compareGrouperRows(
  a: any,
  b: any,
  grouperStructure: any,
  study: any
): number {
  const aValues = getRowSortValues(a, grouperStructure, study);
  const bValues = getRowSortValues(b, grouperStructure, study);
  const length = Math.max(aValues.length, bValues.length);

  for (let i = 0; i < length; i += 1) {
    const aValue = aValues[i] || '';
    const bValue = bValues[i] || '';
    const comparison = compareSortValues(aValue, bValue);
    if (comparison !== 0) {
      return comparison;
    }
  }

  const aOrder = typeof a?.order === 'number' ? a.order : parseInt(a?.order ?? '', 10) || 0;
  const bOrder = typeof b?.order === 'number' ? b.order : parseInt(b?.order ?? '', 10) || 0;
  if (aOrder !== bOrder) {
    return aOrder - bOrder;
  }

  return (a?.grouperId || '').localeCompare(b?.grouperId || '');
}

function resortGrouperRows(
  topic: any,
  grouperStructure: any,
  study: any
): void {
  if (!Array.isArray(topic?.grouper)) return;

  topic.grouper.sort((a: any, b: any) =>
    compareGrouperRows(a, b, grouperStructure, study)
  );

  topic.grouper.forEach((row: any, index: number) => {
    row.order = index + 1;
  });
}

function markDuplicateRows(
  rows: any[],
  targetGrouperId: string | null
): boolean {
  const keyMap = new Map<string, any[]>();

  rows.forEach((row: any) => {
    const key = row.__rowKey || '';
    if (!keyMap.has(key)) {
      keyMap.set(key, []);
    }
    keyMap.get(key)?.push(row);
    row.__duplicate = false;
  });

  let hasDuplicates = false;

  keyMap.forEach((list) => {
    if (list.length > 1) {
      hasDuplicates = true;

      if (targetGrouperId) {
        const target = list.find((row: any) => String(row.__grouperId || '') === targetGrouperId);
        if (target) {
          list.forEach((row: any) => {
            row.__duplicate = true;
          });
          return;
        }
      }

      list.forEach((row: any) => {
        row.__duplicate = true;
      });
    }
  });

  return hasDuplicates;
}

function ensureSequentialRowNumbers(rows: any[]): void {
  if (!Array.isArray(rows)) {
    return;
  }

  rows.forEach((row: any, index: number) => {
    row.order = index + 1;
  });
}

function sortRowsByExistingOrder(rows: any[]): void {
  if (!Array.isArray(rows)) {
    return;
  }

  rows.sort((a: any, b: any) => {
    const aOrder =
      typeof a?.order === 'number' && !Number.isNaN(a.order)
        ? a.order
        : parseInt(String(a?.order ?? ''), 10) || 0;
    const bOrder =
        typeof b?.order === 'number' && !Number.isNaN(b.order)
        ? b.order
        : parseInt(String(b?.order ?? ''), 10) || 0;

    return aOrder - bOrder;
  });
}

function loadPendingNewRows(topic: any): void {
  unsavedGrouperIds.clear();
  if (Array.isArray(topic?.__pendingNewGrouperIds)) {
    topic.__pendingNewGrouperIds.forEach((id: any) => {
      if (id !== undefined && id !== null) {
        unsavedGrouperIds.add(String(id));
      }
    });
  }
}

function updateTopicPendingNewRows(topic: any): void {
  if (!topic) return;
  topic.__pendingNewGrouperIds = Array.from(unsavedGrouperIds);
}

function markRowAsNew(topic: any, grouperId: string | number | undefined | null): void {
  if (!topic || grouperId === undefined || grouperId === null) return;
  unsavedGrouperIds.add(String(grouperId));
  updateTopicPendingNewRows(topic);
}

function removeRowFromNewMarkers(topic: any, grouperId: string | number | undefined | null): void {
  if (!topic || grouperId === undefined || grouperId === null) return;
  unsavedGrouperIds.delete(String(grouperId));
  updateTopicPendingNewRows(topic);
}

function clearNewRowMarkers(topic: any): void {
  if (!topic) return;
  unsavedGrouperIds.clear();
  topic.__pendingNewRowIds = [];
}

function rebuildRowDataFromTopic(
  topic: any,
  grouperStructure: any,
  study: any,
  preserveScroll: boolean = false
): void {
  if (!topic || !grouperStructure || !study) return;

  // Save scroll position if requested
  let scrollLeft = 0;
  let scrollTop = 0;
  if (preserveScroll && gridApi && gridDiv) {
    const api: any = gridApi;
    // Get scroll position from the grid's viewport
    const viewport = gridDiv.querySelector('.ag-body-viewport') as HTMLElement;
    if (viewport) {
      scrollLeft = viewport.scrollLeft;
      scrollTop = viewport.scrollTop;
    }
  }

  const grouperRows = ensureGrouperArray(topic.grouper, `grid sheet topic ${topicId}`);
  ensureGrouperRows(grouperRows, grouperStructure, topicId);

  if (grouperRows.length === 0) {
    const emptyRow: any = {
      row: '1',
      _rowIndex: 0,
      __grouperRowId: '',
      grouperIdDisplay: '-',
      nameDisplay: 'Study'
    };

    const structureLevels = grouperStructure.levels || [];
    structureLevels.forEach((level: any, levelIndex: number) => {
      const levelNumber = String(level.level ?? level.number ?? levelIndex + 1);

      level.columns?.forEach((columnDef: any) => {
        emptyRow[columnDef.id] = '-';
      });

      level.sources?.forEach((source: any) => {
        if (source.refTable === 'study' || source.refType === 'Study') {
          return;
        }
        const fieldName = getSourceFieldName(source) || (levelNumber === '1' ? 'custom' : '');
        if (!fieldName) return;
        emptyRow[fieldName] = '-';
      });
    });

    rowData = [emptyRow];
    hasDuplicateRows = false;
  } else {
    rowData = grouperRows.map((grouperRow: any, index: number) =>
      createRowDataFromGrouperRow(grouperRow, index, grouperStructure, study)
    );

    hasDuplicateRows = markDuplicateRows(rowData, lastEditedGrouperId);
  }

  if (gridApi) {
    const api: any = gridApi;
    if (typeof api.setRowData === 'function') {
      api.setRowData(rowData);
    } else if (typeof api.setGridOption === 'function') {
      api.setGridOption('rowData', rowData);
    }

    // Restore scroll position if requested
    if (preserveScroll && gridDiv) {
      // Use requestAnimationFrame to ensure DOM is updated before restoring scroll
      window.requestAnimationFrame(() => {
        const viewport = gridDiv.querySelector('.ag-body-viewport') as HTMLElement;
        if (viewport) {
          viewport.scrollLeft = scrollLeft;
          viewport.scrollTop = scrollTop;
        }
      });
    }
  }
}

  function initializeGrid() {
    if (!gridDiv || !$studyExtractStore || !$projectStructureStore || !topicId) return;

    // Destroy existing grid
    if (gridInstance) {
      gridInstance.destroy();
      gridInstance = null;
      gridApi = null;
    }

    // Clear selected rows when reinitializing (reassign to trigger reactivity)
    selectedRows = new Set();

    // Get topic structure and grouper data
    const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
    const grouperStructure = topicStructure?.grouper;

    ensureGrouperStructureExists(grouperStructure, topicId);

    const topic = $studyExtractStore.topics?.find((t: any) => t.id === topicId);
    assertTopicExists(topic, topicId);
    loadPendingNewRows(topic);

    if (topicId === 'baseline') {
      console.debug(
        '[GrouperEditor] Baseline topic snapshot on open:',
        JSON.stringify(topic, null, 2)
      );
    }

    if (!topic.grouper) {
      topic.grouper = [];
    }

    const grouperRows = ensureGrouperArray(topic.grouper, `grid sheet topic ${topicId}`);
    ensureGrouperRows(grouperRows, grouperStructure, topicId);

    if (grouperRows.length > 0) {
      sortRowsByExistingOrder(grouperRows);
      if (topicId === 'baseline') {
        console.debug(
          '[GrouperEditor] Baseline grouper snapshot on open:',
          JSON.stringify(topic.grouper, null, 2)
        );
      }
    } else if (topicId === 'baseline') {
      console.debug(
        '[GrouperEditor] Baseline grouper snapshot on open (no rows):',
        JSON.stringify(topic.grouper, null, 2)
      );
    }

    const study = $studyExtractStore;

    // Build column definitions
    columnDefs = buildColumnDefs(grouperStructure);
    console.debug('[GrouperEditor] columnDefs', columnDefs);

    rebuildRowDataFromTopic(topic, grouperStructure, study);

    console.debug('[GrouperEditor] rowData snapshot for topic', topicId, rowData);

    // Create grid
    const gridOptions: GridOptions = {
      columnDefs: columnDefs as any,
      rowData,
      domLayout: 'normal',
      suppressCellFocus: false,
      stopEditingWhenCellsLoseFocus: true,
      headerHeight: 40,
      autoSizeStrategy: {
        type: 'fitCellContents',
      },
      suppressColumnVirtualisation: true,
      suppressRowVirtualisation: true,
      suppressMovableColumns: true,
      suppressContextMenu: true,
      rowSelection: {
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: true,
        enableClickSelection: true
      },
      // Configure selection column to be pinned left (first column)
      selectionColumnDef: {
        pinned: 'left',
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        resizable: false,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        lockPosition: true
      },
      rowDragMultiRow: true,
      rowDragManaged: true,
      animateRows: true,
      defaultColDef: {
        editable: true,
        resizable: false,
        sortable: false,
        filter: false
      },
      onGridReady: (params: any) => {
        gridApi = params.api;
      },
      onSelectionChanged: (params: any) => {
        handleSelectionChanged(params);
      },
      onCellValueChanged: (params: any) => {
        handleCellChange(params);
      },
      onDisplayedColumnsChanged: () => {
        window.requestAnimationFrame(() => gridApi?.refreshCells({ force: true }));
      },
      onRowDragEnd: (params: any) => {
        handleRowDragEnd(params);
      }
      // Removed onCellClicked - using agRichSelectCellEditor instead of custom popup
    };

    gridInstance = createGrid(gridDiv, gridOptions as any);
  }

  function buildColumnDefs(grouperStructure: any): any[] {
    const columnDefs: any[] = [
      {
        field: 'row',
        headerName: 'ROW',
        editable: false,
        width: 88,
        minWidth: 88,
        maxWidth: 88,
        pinned: 'left',
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        resizable: false,
        sortable: false,
        filter: false,
        rowDrag: true,
        cellRenderer: (params: any) => {
          const value = params.node ? String(params.node.rowIndex + 1) : params.value || '-';
          const duplicateIcon = params.data?.__duplicate
            ? '<span class="row-duplicate-icon" title="Duplicate row"><svg class="w-4 h-4 no-match-icon" viewBox="0 0 20 20"><path fill="currentColor" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path></svg></span>'
            : '';
          return `<div class="row-number-cell"><span class="row-number-value">${value}</span>${duplicateIcon}</div>`;
        },
        cellStyle: {
          'text-align': 'center',
          'padding': '0'
        }
      },
      {
        field: 'nameDisplay',
        headerName: 'NAME',
        editable: false,
        width: 200,
        minWidth: 200,
        maxWidth: 200,
        pinned: 'left',
        suppressSizeToFit: true,
        suppressAutoSize: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        resizable: false,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          const value = params.value || 'Study';
          if (!params.data?.__isNew) {
            return value;
          }
          return `<span class="grouper-name-pill-wrap">${value}<span class="grouper-new-pill">NEW</span></span>`;
        },
        cellStyle: {
          'padding': '8px 8px'
        }
      }
    ];

    const customColumns: any[] = [];
    (grouperStructure.levels || []).forEach((level: any, levelIndex: number) => {
      const levelNumber = String(level.level ?? level.number ?? levelIndex + 1);
      const levelLabel = level.label || `Level ${levelNumber}`;
      const levelColumns: any[] = [];

      const addReferenceColumn = (source: any) => {
        const fieldName = getSourceFieldName(source);
        const options = getSourceOptions(source.refTable, source.refId);
        const idToNameMap = new Map(options.map((o: any) => [o.id, o.name]));
        const nameToIdMap = new Map(options.map((o: any) => [o.name, o.id]));
        const values = ['-', ...options.map((o: any) => o.name)];

        return {
          field: fieldName,
          headerName: source.refType?.toUpperCase() || source.refTable?.toUpperCase(),
          editable: true,
          cellEditor: 'agRichSelectCellEditor',
          cellEditorPopup: true,
          cellEditorParams: {
            values,
            allowTyping: false,
            filterList: true,
            searchType: 'match',
            highlightMatch: true,
            valueListMaxHeight: 220
          },
          context: {
            levelNumber,
            sourceDef: source,
            refTable: source.refTable,
            refId: source.refId,
            idToNameMap,
            nameToIdMap
          },
          valueGetter: (params: any) => {
            const value = params.data[fieldName];
            if (!value || value === '-') return '-';
            return idToNameMap.get(value) || value;
          },
          valueSetter: (params: any) => {
            let value = params.newValue === '' || params.newValue === null || params.newValue === undefined ? '-' : params.newValue;
            if (value !== '-') {
              value = nameToIdMap.get(value) || value;
            }
            params.data[fieldName] = value;
            return true;
          },
          cellRenderer: (params: any) => {
            if (!params.value || params.value === '-') return '-';
            let option = options.find((o: any) => o.id === params.value);
            if (!option) {
              const dynamicOptions = getSourceOptions(source.refTable, source.refId);
              option = dynamicOptions.find((o: any) => o.id === params.value);
            }
            return option?.name || params.value;
          },
          width: 200,
          minWidth: 150,
          suppressHeaderMenuButton: true,
          suppressHeaderContextMenu: true,
          resizable: false,
          sortable: false,
          filter: false,
          cellStyle: {
            'padding': '8px 8px',
            'cursor': 'pointer'
          }
        };
      };

      const addTextColumn = (columnDef: any) => ({
        field: columnDef.id,
        headerName: columnDef.label || columnDef.id,
        editable: true,
        cellEditor: 'agTextCellEditor',
        cellEditorParams: {
          maxLength: 5000
        },
        width: 200,
        minWidth: 150,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        resizable: false,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => params.value || '-',
        cellStyle: {
          'padding': '8px 8px',
          'cursor': 'text'
        },
        context: {
          levelNumber
        }
      });

      level.columns?.forEach((columnDef: any) => {
        levelColumns.push(addTextColumn(columnDef));
      });

      if (level.sources) {
        level.sources.forEach((source: any) => {
          if (source.refTable === 'study' || source.refType === 'Study') {
            return;
          }

          if (source.type === 'reference') {
            levelColumns.push(addReferenceColumn(source));
          } else if (source.type === 'custom') {
            if (levelNumber === '1') {
              customColumns.push({
                field: getSourceFieldName(source) || 'custom',
                headerName: 'CUSTOM',
                editable: true,
                cellEditor: 'agTextCellEditor',
                cellEditorParams: {
                  maxLength: 5000
                },
                width: 200,
                minWidth: 150,
                suppressHeaderMenuButton: true,
                suppressHeaderContextMenu: true,
                resizable: false,
                sortable: false,
                filter: false,
                cellRenderer: (params: any) => params.value || '-',
                cellStyle: {
                  'padding': '8px 8px',
                  'cursor': 'text'
                },
                context: {
                  levelNumber
                }
              });
            } else {
              logWarning(`[GrouperEditor] Custom source ignored for level ${levelNumber} (only level 1 supported)`, "GrouperEditor", undefined);
            }
          }
        });
      }

      if (levelColumns.length === 0 && levelNumber !== '1') {
        return;
      }

      const headerLabel = levelLabel.toUpperCase();

      if (levelNumber === '1') {
        if (levelColumns.length > 0) {
          columnDefs.push({
            headerName: headerLabel,
            children: levelColumns,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true
          });
        }
      } else {
        if (levelColumns.length === 1) {
          const singleColumn = {
            ...levelColumns[0],
            headerName: headerLabel
          };
          columnDefs.push(singleColumn);
        } else if (levelColumns.length > 1) {
          columnDefs.push({
            headerName: headerLabel,
            children: levelColumns,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true
          });
        }
      }
    });

    columnDefs.push({
      field: 'custom',
      headerName: 'CUSTOM',
      editable: true,
      cellEditor: 'agTextCellEditor',
      cellEditorParams: {
        maxLength: 5000
      },
      width: 200,
      minWidth: 150,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      resizable: false,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => params.value || '-',
      cellStyle: {
        'padding': '8px 8px',
        'cursor': 'text'
      },
      context: {
        levelNumber: 'custom'
      }
    });

    if (customColumns.length > 0) {
      columnDefs.push(...customColumns);
    }

    // Add root-level grouper columns (e.g., sample_size, analysis_set, etc.)
    const rootGrouperColumns = $projectStructureStore?.grouper?.columns || [];
    rootGrouperColumns.forEach((columnDef: any) => {
      const editorConfig = getCellEditorConfig(columnDef, $projectStructureStore);
      const lookupValueConfig = getLookupValueGetterSetter(columnDef, $projectStructureStore);
      const lookupRenderer = getLookupCellRenderer(columnDef, $projectStructureStore);

      const grouperColumnDef: any = {
        field: columnDef.id,
        headerName: columnDef.label || columnDef.id,
        editable: true,
        width: 200,
        minWidth: 150,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        resizable: false,
        sortable: false,
        filter: false,
        ...editorConfig,
        context: {
          isGrouperColumn: true,
          columnDef: columnDef
        },
        valueGetter: (params: any) => {
          const value = params.data[columnDef.id];
          if (value === '-' || value === undefined || value === null) return '';
          if (lookupValueConfig?.valueGetter) {
            return lookupValueConfig.valueGetter(params);
          }
          return value;
        },
        valueSetter: (params: any) => {
          let value = params.newValue === '' || params.newValue === null || params.newValue === undefined ? '-' : params.newValue;
          if (lookupValueConfig?.valueSetter) {
            return lookupValueConfig.valueSetter(params);
          }
          params.data[columnDef.id] = value;
          return true;
        },
        cellRenderer: (params: any) => {
          let displayValue = params.value || '-';
          if (lookupRenderer && displayValue !== '-') {
            displayValue = lookupRenderer(params) || '-';
          }
          return displayValue;
        },
        cellStyle: {
          'padding': '8px 8px',
          'cursor': columnDef.type === 'lookup' ? 'pointer' : 'text'
        }
      };

      columnDefs.push(grouperColumnDef);
    });

    return columnDefs;
  }

  function getSourceOptions(refTable: string, refId: string): Array<{ id: string; name: string }> {
    
    if (!$studyExtractStore?.references) {
      logWarning('[GrouperEditor] getSourceOptions - No references in store', "GrouperEditor", undefined);
      return [];
    }

    
    const collection = $studyExtractStore.references.find((ref: any) => ref.id === refTable);
    if (!collection) {
      logWarning(`[GrouperEditor] getSourceOptions - Collection not found for refTable: ${refTable}`, "GrouperEditor", undefined);
      return [];
    }


    if (collection.rows) {
      const options = collection.rows.map((row: any) => {
        const nameCell = row.cells?.find((c: any) => c.id === refId || c.id === 'name' || c.id === 'title');
        const displayName = nameCell?.value || row.id;
        
        return {
          id: row.id,
          name: displayName
        };
      });
      return options;
    }

    if (collection.values) {
      return collection.values.map((value: any) => ({
        id: value.id,
        name: value.value || value.id
      }));
    }

    return [];
  }

  function handleSelectionChanged(params: any) {
    if (!gridApi) return;
    
    // Get all selected nodes
    const selectedNodes = gridApi.getSelectedNodes();
    
    // Create new Set to ensure Svelte reactivity
    const newSelectedRows = new Set<number>();
    
    // Update selectedRows Set with row indices
    selectedNodes.forEach((node: any) => {
      const rowIndex = node.data?._rowIndex;
      if (rowIndex !== undefined) {
        newSelectedRows.add(rowIndex);
      }
    });
    
    // Reassign to trigger reactivity
    selectedRows = newSelectedRows;
  }

  function handleRowDragEnd(params: any) {
    if (!$studyExtractStore || !$projectStructureStore || !topicId) return;

    const api = params?.api || gridApi;
    if (!api) return;

    const orderedKeys: string[] = [];
    const displayedRowCount = api.getDisplayedRowCount();

    for (let i = 0; i < displayedRowCount; i += 1) {
      const node = api.getDisplayedRowAtIndex(i);
      if (!node?.data) continue;
      if (node.data.__grouperRowId) {
        orderedKeys.push(String(node.data.__grouperRowId));
      } else if (node.data._rowIndex !== undefined) {
        orderedKeys.push(`row-${node.data._rowIndex}`);
      } else {
        orderedKeys.push(`row-${i}`);
      }
    }

    if (orderedKeys.length === 0) return;

    const updatedStore = JSON.parse(JSON.stringify($studyExtractStore));
    const topic = updatedStore.topics?.find((t: any) => t.id === topicId);

    if (!topic?.grouper) return;

    const rowMap = new Map<string, any>();
    topic.grouper.forEach((row: any, idx: number) => {
      const key = row?.grouperId ? String(row.grouperId) : `row-${idx}`;
      rowMap.set(key, row);
    });

    const reorderedRows: any[] = [];
    orderedKeys.forEach((key: string) => {
      const row = rowMap.get(key);
      if (row) {
        reorderedRows.push(row);
        rowMap.delete(key);
      }
    });

    rowMap.forEach((row: any) => {
      reorderedRows.push(row);
    });

    ensureSequentialRowNumbers(reorderedRows);
    topic.grouper = reorderedRows;

    selectedRows = new Set();

    studyExtractStore.set(updatedStore);
    saveTargetExtract('study', 'study_1', updatedStore);

    setTimeout(() => {
      initializeGrid();
    }, 100);
  }

  // Removed custom popup functions - using agRichSelectCellEditor instead

  function handleCellChange(params: any) {
    const { colDef, newValue, data, column } = params;
    
    if (!$projectStructureStore || !topicId || !$studyExtractStore) return;

    const updatedStore = JSON.parse(JSON.stringify($studyExtractStore));
    const topic = updatedStore.topics?.find((t: any) => t.id === topicId);
    if (!topic) return;

    const grouperStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId)?.grouper;
    if (!grouperStructure) return;

    const actualRowIndex = data._rowIndex !== undefined ? data._rowIndex : params.rowIndex;
    ensureGrouperStructureExists(grouperStructure, topicId);

    if (!Array.isArray(topic.grouper)) {
      throw new Error(`[grouper] Topic "${topicId}" has invalid grouper array during edit`);
    }

    while (topic.grouper.length <= actualRowIndex) {
      const nextId = String(topic.grouper.length + 1);
      topic.grouper.push(createEmptyGrouperRow(grouperStructure, nextId, topic.grouper.length + 1));
    }

    const grouperRow = topic.grouper[actualRowIndex];
    if (!grouperRow) return;

    lastEditedGrouperId = grouperRow.grouperId ? String(grouperRow.grouperId) : null;

    const field = colDef.field;
    
    if (!field || field === 'row') {
      return;
    }
    
    // Try to get context from column.getColDef() first (more reliable), then fall back to colDef.context
    const columnDef = column?.getColDef ? column.getColDef() : colDef;
    const context = columnDef.context || colDef.context || {};
    const levelNumber = context.levelNumber || '1';
    let sourceDef = context.sourceDef;

    // Handle grouper column cell changes (e.g., sample_size, analysis_set, etc.)
    if (context.isGrouperColumn) {
      const grouperColumnDef = context.columnDef;
      if (!grouperColumnDef) return;

      // Ensure cells array exists
      if (!Array.isArray(grouperRow.cells)) {
        grouperRow.cells = [];
      }

      // Find or create the cell
      let cell = grouperRow.cells.find((c: any) => c.id === field);
      const cellValue = newValue === '-' || newValue === '' || newValue === null || newValue === undefined ? '' : String(newValue);

      if (cell) {
        cell.value = cellValue;
      } else {
        cell = {
          id: field,
          value: cellValue,
          sourceInfo: {},
          reviewInfo: {}
        };
        grouperRow.cells.push(cell);
      }

      // Update data for display
      data[field] = cellValue || '-';

      ensureGrouperRows(topic.grouper, grouperStructure, topicId);

      studyExtractStore.set(updatedStore);
      saveTargetExtract('study', 'study_1', updatedStore);

      // Update grid data without reinitializing to preserve scroll position
      rebuildRowDataFromTopic(topic, grouperStructure, updatedStore, true);
      return;
    }

    if (field === 'custom') {
      const customValue =
        typeof newValue === 'string'
          ? newValue
          : typeof data[field] === 'string'
            ? data[field]
            : '';
      const normalizedCustom = customValue === '-' ? '' : customValue;
      data.custom = normalizedCustom || '-';
      setCustomValue(grouperRow, normalizedCustom);
      ensureGrouperRows(topic.grouper, grouperStructure, topicId);

      studyExtractStore.set(updatedStore);
      saveTargetExtract('study', 'study_1', updatedStore);

      // Update grid data without reinitializing to preserve scroll position
      rebuildRowDataFromTopic(topic, grouperStructure, updatedStore, true);
      return;
    }

    const levelStructure = (grouperStructure.levels || []).find((lvl: any, idx: number) => {
      const identifier = String(lvl.level ?? lvl.number ?? idx + 1);
      return identifier === levelNumber;
    });

    if (!levelStructure) {
      throw new Error(`[grouper] Level "${levelNumber}" not defined for topic "${topicId}"`);
    }

    const referenceSources = getReferenceSources(levelStructure);

    if (!sourceDef && field !== 'custom') {
      const fallbackSource = referenceSources.find((source: any) => getSourceFieldName(source) === field);
      if (fallbackSource) {
        sourceDef = fallbackSource;
      }
    }

    if (!sourceDef && levelStructure?.sources) {
      const customSource = levelStructure.sources.find((s: any) => s?.type === 'custom');
      if (customSource) {
        const customField = getSourceFieldName(customSource) || 'custom';
        if (field === customField) {
          sourceDef = customSource;
        }
      }
    }

    if (sourceDef && sourceDef.type === 'reference') {
      const sourceIndex = referenceSources.findIndex((source: any) => {
        if (source === sourceDef) return true;
        return (
          source?.refTable === sourceDef.refTable &&
          source?.refType === sourceDef.refType &&
          source?.refId === sourceDef.refId
        );
      });

      if (sourceIndex === -1) {
        return;
      }

      const levelEntry = ensureRowLevelEntry(grouperRow, levelNumber, referenceSources);
      const cellValue = data[field] || newValue;
      const normalizedValue =
        cellValue === undefined || cellValue === null || cellValue === '-' ? '' : String(cellValue);
      levelEntry.refs[sourceIndex] = normalizedValue;
    } else if (sourceDef && sourceDef.type === 'custom') {
      const customValue =
        typeof newValue === 'string'
          ? newValue
          : typeof data[field] === 'string'
            ? data[field]
            : '';
      const normalizedCustom = customValue === '-' ? '' : customValue;
      setCustomValue(grouperRow, normalizedCustom);
    } else {
      return;
    }

    ensureGrouperRows(topic.grouper, grouperStructure, topicId);

    studyExtractStore.set(updatedStore);
    saveTargetExtract('study', 'study_1', updatedStore);
    
    // Update grid data without reinitializing to preserve scroll position
    rebuildRowDataFromTopic(topic, grouperStructure, updatedStore, true);
  }

  function handleSortRows() {
    if (!$studyExtractStore || !$projectStructureStore || !topicId) return;

    const updatedStore = JSON.parse(JSON.stringify($studyExtractStore));
    const topic = updatedStore.topics?.find((t: any) => t.id === topicId);

    if (!topic) return;

    const grouperStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId)?.grouper;
    ensureGrouperStructureExists(grouperStructure, topicId);

    if (!Array.isArray(topic.grouper) || topic.grouper.length === 0) {
      return;
    }

    const levels = Array.isArray(grouperStructure.levels) ? grouperStructure.levels : [];
    const levelCount = levels.length;

    if (levelCount <= 1) {
      // Single-level grouper (e.g. Baseline) - fallback to default sort
      resortGrouperRows(topic, grouperStructure, updatedStore);
    } else {
      // Multi-level: cycle primary sort level across clicks
      if (lastSortTopicId !== topicId) {
        // New topic context - start from level 0
        lastSortTopicId = topicId;
        currentSortPrimaryLevelIndex = null;
      }

      let nextPrimary = currentSortPrimaryLevelIndex;
      if (nextPrimary === null) {
        nextPrimary = 0;
      } else {
        nextPrimary = (nextPrimary + 1) % levelCount;
      }
      currentSortPrimaryLevelIndex = nextPrimary;

      const study = updatedStore;
      const primaryIndex = nextPrimary;

      topic.grouper.sort((a: any, b: any) => {
        const aLevels = getRowSortValuesByLevel(a, grouperStructure, study);
        const bLevels = getRowSortValuesByLevel(b, grouperStructure, study);

        const order: number[] = [];
        for (let i = 0; i < levelCount; i += 1) {
          order.push((primaryIndex + i) % levelCount);
        }

        // Compare by levels in the rotated order
        for (const levelIndex of order) {
          const aVals = aLevels[levelIndex] || [];
          const bVals = bLevels[levelIndex] || [];
          const maxLen = Math.max(aVals.length, bVals.length);

          for (let i = 0; i < maxLen; i += 1) {
            const aVal = aVals[i] || '';
            const bVal = bVals[i] || '';
            const cmp = compareSortValues(aVal, bVal);
            if (cmp !== 0) {
              return cmp;
            }
          }
        }

        // Finally compare by custom value as last tie-breaker
        const aCustom = getCustomValue(a);
        const bCustom = getCustomValue(b);
        const customCmp = compareSortValues(aCustom, bCustom);
        if (customCmp !== 0) {
          return customCmp;
        }

        const aOrder = typeof a?.order === 'number' ? a.order : parseInt(a?.order ?? '', 10) || 0;
        const bOrder = typeof b?.order === 'number' ? b.order : parseInt(b?.order ?? '', 10) || 0;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }

        return (a?.grouperId || '').localeCompare(b?.grouperId || '');
      });

      topic.grouper.forEach((row: any, index: number) => {
        row.order = index + 1;
      });
    }

    ensureGrouperRows(topic.grouper, grouperStructure, topicId);
    selectedRows = new Set();

    studyExtractStore.set(updatedStore);
    saveTargetExtract('study', 'study_1', updatedStore);

    rebuildRowDataFromTopic(topic, grouperStructure, updatedStore);
  }

  function handleAddRow() {
    if (!$studyExtractStore || !$projectStructureStore || !topicId) return;

    const updatedStore = JSON.parse(JSON.stringify($studyExtractStore));
    const topic = updatedStore.topics?.find((t: any) => t.id === topicId);
    
    if (!topic) return;

    const grouperStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId)?.grouper;
    ensureGrouperStructureExists(grouperStructure, topicId);

    if (!Array.isArray(topic.grouper)) {
      topic.grouper = [];
    }

    const newRowIndex = topic.grouper.length + 1;
    const newGrouperId = String(newRowIndex);
    const newRow = createEmptyGrouperRow(grouperStructure, newGrouperId, newRowIndex);

    topic.grouper.push(newRow);
    markRowAsNew(topic, newGrouperId);

    lastEditedGrouperId = newGrouperId;

    ensureSequentialRowNumbers(topic.grouper);
    ensureGrouperRows(topic.grouper, grouperStructure, topicId);

    // Sync the new grouper row to all topic tables
    syncGrouperRowToTables(topicId, newRow, 'add', updatedStore, $projectStructureStore);

    studyExtractStore.set(updatedStore);
    saveTargetExtract('study', 'study_1', updatedStore);

    rebuildRowDataFromTopic(topic, grouperStructure, updatedStore);
  }

  function handleDeleteRows() {
    if (!gridApi || !$studyExtractStore || !$projectStructureStore || !topicId) return;

    const selectedNodes = gridApi.getSelectedNodes();
    if (selectedNodes.length === 0) {
      alert('Please select rows to delete');
      return;
    }

    const rowIndices = selectedNodes
      .map((node: any) => node.data?._rowIndex)
      .filter((idx: number) => idx !== undefined)
      .sort((a: number, b: number) => b - a); // Sort descending to delete from end

    if (rowIndices.length === 0) return;

    const updatedStore = JSON.parse(JSON.stringify($studyExtractStore));
    const topic = updatedStore.topics?.find((t: any) => t.id === topicId);
    
    if (!topic) return;
    const grouperStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId)?.grouper;
    ensureGrouperStructureExists(grouperStructure, topicId);

    if (!Array.isArray(topic.grouper)) {
      throw new Error(`[grouper] Topic "${topicId}" has invalid grouper array during delete`);
    }

    if (!Array.isArray(topic.grouper)) {
      return;
    }

    // Extract grouper row IDs before deleting (for sync)
    const grouperRowsToDelete = rowIndices
      .map((idx: number) => {
        if (idx >= 0 && idx < topic.grouper.length) {
          return topic.grouper[idx];
        }
        return null;
      })
      .filter((row: any) => row !== null);

    // Sync deletion to all topic tables before removing from grouper
    if (grouperRowsToDelete.length > 0) {
      syncGrouperRowsToTables(topicId, grouperRowsToDelete, 'delete', updatedStore, $projectStructureStore);
    }

    // Delete rows from end to start to maintain indices
    rowIndices.forEach((idx: number) => {
      if (idx >= 0 && idx < topic.grouper.length) {
        const removed = topic.grouper[idx];
        removeRowFromNewMarkers(topic, removed?.grouperId);
        topic.grouper.splice(idx, 1);
      }
    });

    ensureSequentialRowNumbers(topic.grouper);
    ensureGrouperRows(topic.grouper, grouperStructure, topicId);

    studyExtractStore.set(updatedStore);
    saveTargetExtract('study', 'study_1', updatedStore);

    rebuildRowDataFromTopic(topic, grouperStructure, updatedStore);
  }

  function handleSaveAndClose() {
    if (hasDuplicateRows) {
      logWarning('Cannot save grouper while duplicate groups are present.', "GrouperEditor", undefined);
      return;
    }
    // Persist clearing of new markers
    const updatedStore = JSON.parse(JSON.stringify($studyExtractStore));
    const topic = updatedStore.topics?.find((t: any) => t.id === topicId);
    if (topic) {
      clearNewRowMarkers(topic);
    }
    studyExtractStore.set(updatedStore);
    saveTargetExtract('study', 'study_1', updatedStore);
    show = false;
    currentSortPrimaryLevelIndex = null;
    lastSortTopicId = null;
  }

  function handleCancel() {
    show = false;
    currentSortPrimaryLevelIndex = null;
    lastSortTopicId = null;
  }

  onDestroy(() => {
    if (gridInstance) {
      gridInstance.destroy();
      gridInstance = null;
      gridApi = null;
    }
  });
</script>

<div class="grouper-editor editor-content">

  <div class="editor-header">
    <span class="flex items-center gap-5">
      <div class="editor-header-container">
        <span class="editor-header-label">Grouper:</span>
        <h2 class="editor-header-title">{topicId}</h2>
      </div>
    </span>
    <div class="editor-actions">
      <Button class="save-button" size="xs" color="primary" disabled={hasDuplicateRows} title={hasDuplicateRows ? 'Resolve duplicate groups before saving' : undefined} on:click={handleSaveAndClose}>
        <FontAwesomeIcon icon={faSave} class="mr-2" />Save & Close
      </Button>
      <Button class="cancel-button" size="xs" color="light" on:click={handleCancel}>
        <FontAwesomeIcon icon={faTimes} class="mr-2" />Cancel
      </Button>
    </div>
  </div>

  <div class="tool-controls">
    <div class="tool-group">
      <div class="tool-buttons">
        <Button class="tool-button" size="xs" color="light" title="Add Row" on:click={handleAddRow}><CustomImage id="table-row-add" size={24} /></Button>
        <Button class="tool-button" size="xs" color="light" title="Remove Row" disabled={!rowRemoveEnabled} on:click={handleDeleteRows}><CustomImage id="table-row-remove" size={24} /></Button>
        <Button class="tool-button with-text" size="xs" color="light" title="Sort Rows" on:click={handleSortRows}>
          <FontAwesomeIcon icon={faArrowDownAZ} class="mr-2" />Sort
        </Button>
      </div>
    </div>
  </div>

  <div class="editor-container">
    <div class="grouper-grid">
      <div bind:this={gridDiv} class="grouper-editor ag-theme-quartz ag-grouper-grid" data-ag-theme-mode="dark"></div>
    </div>
  </div>

</div>
